import { TextField } from "@mui/material"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import { StyledButton } from "components/Button/StyledButton"
import { Project } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { useSnackbar } from "notistack"
import { useEffect, useState } from "react"
import { useParams } from "react-router"
import { ContactClientCard } from "./ContactClientCard"

type ContactClientDialogProps = {
  open: boolean
  onClose: () => void
  isOpportunity?: boolean
}

export const ContactClientDialog = (props: ContactClientDialogProps) => {
  const { open, onClose, isOpportunity = false } = props
  const { projectID } = useParams()
  const projectsState = useStoreState((state) => state.projects)
  const contacts = useStoreState((state) => state.contacts)
  const updateProject = useStoreActions((state) => state.updateProject)
  const { enqueueSnackbar } = useSnackbar()

  const [project, setProject] = useState<Project | null>(null)
  const [contactNote, setContactNote] = useState<string>("") // State for the note text

  useEffect(() => {
    const project = projectsState.find((project) =>
      isOpportunity ? project?.lead.opportunityID === projectID : project?.id === projectID
    )
    if (project) {
      setProject(project)
      // Load existing notes if they exist
      setContactNote(project.notes || "")
    }
  }, [projectID, projectsState, isOpportunity])

  const handleSave = () => {
    if (project && contactNote.trim()) {
      // Log for debugging
      console.log("=== SAVING CONTACT NOTE ===", {
        projectId: project.id,
        noteContent: contactNote,
        timestamp: new Date().toISOString(),
      })

      // Update the project with the contact notes
      const updatedProject = {
        ...project,
        notes: contactNote,
        updatedDate: new Date().toISOString(),
      }

      updateProject(updatedProject)

      // Show success notification
      enqueueSnackbar("Kontaktnotiz erfolgreich gespeichert", { variant: "success" })

      // Close the dialog
      onClose()
    } else if (!contactNote.trim()) {
      // Show warning if trying to save empty note
      enqueueSnackbar("Bitte geben Sie eine Notiz ein", { variant: "warning" })
    }
  }

  const handleCancel = () => {
    // Reset note to original value if canceling
    setContactNote(project?.notes || "")
    onClose()
  }

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle sx={{ px: 1 }}>Kunden kontaktieren</DialogTitle>
      <DialogContent sx={{ p: 1 }}>
        {project?.contacts.map((contact) => {
          const singleContact = contacts.find((item) => item.contactID === contact.contactID)
          if (singleContact) {
            return <ContactClientCard key={contact.contactID} contact={singleContact} />
          }
          return null
        })}

        <TextField
          label="Ergebnis dokumentieren"
          variant="outlined"
          fullWidth
          multiline
          rows={4}
          value={contactNote}
          onChange={(e) => setContactNote(e.target.value)}
          placeholder="Beschreiben Sie das Ergebnis des Kundenkontakts..."
        />
      </DialogContent>
      <DialogActions>
        <StyledButton buttonType="cancel" fullWidth variant="contained" onClick={handleCancel} />
        <StyledButton buttonType="save" fullWidth variant="contained" onClick={handleSave} />
      </DialogActions>
    </Dialog>
  )
}
